import {
  Box,
  Button,
  Switch,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { useState } from 'react';

import { useTimeOff } from './hooks';
import { AddTimeOff, TimeOffTable } from './components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MultiselectSearchableField } from 'components/Multiselect';
import { TOOLBAR_FIELD_GAP, toolbarFieldSx } from '@ui';
import { BeachAccess } from '@mui/icons-material';
import { t } from 'i18next';
import { ColumnDensity } from '@common/types';
import { ColumnDensityFilter } from 'components/TablePreferences';

export const TimeOff = () => {
  const {
    entries,
    count,
    isLoading,
    filters,
    handleFilterChange,
    handleOwnTasksOnlyChange,
    userOptions,
    handleSearchUserIdChange,
    handleResetFilters,
    isFiltersEmpty,
    isInitialFilters,
    manageTimeOffPermission,
    setFilters,
  } = useTimeOff();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [density, setDensity] = useState<ColumnDensity>('comfortable');

  const dateRangeValue: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [
    filters.start_date ? dayjs(filters.start_date) : null,
    filters.end_date ? dayjs(filters.end_date) : null,
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          mt: '1rem',
          mb: '2rem',
          width: '100%',
          justifyContent: 'space-between',
          bgcolor: '',
        }}
      >
        <Typography variant='h4'>Time off</Typography>
        <Box>
          <AddTimeOff variant='create' disabled={!manageTimeOffPermission} />
        </Box>
      </Box>

      {entries.length === 0 && isInitialFilters && !isLoading ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <BeachAccess sx={{ width: 56, height: 56, mb: 2 }} color='primary' />
          <Typography variant='h6'>
            {t('timeOff.table.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.disabled' }}>
            {t('timeOff.table.noResults.subTitle')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              bgcolor: 'white',
              padding: '1rem',
              border: theme => `1px solid ${theme.palette.grey[300]}`,
              borderBottom: 'none',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                boxSizing: 'border-box',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: TOOLBAR_FIELD_GAP,
                  alignItems: 'center',
                }}
              >
                <MultiselectSearchableField
                  name={'user_id'}
                  options={userOptions}
                  placeholder={'User'}
                  value={filters.user_ids ?? []}
                  handleChange={handleSearchUserIdChange}
                  optional
                  sx={toolbarFieldSx}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePicker
                    value={dateRangeValue}
                    onChange={([start, end]) => {
                      handleFilterChange({
                        start_date: start?.format('YYYY-MM-DD') ?? '',
                        end_date: end?.format('YYYY-MM-DD') ?? '',
                      });
                    }}
                    localeText={{
                      start: 'Start date',
                      end: 'End date',
                    }}
                    slotProps={{
                      textField: { size: 'medium', sx: toolbarFieldSx },
                    }}
                  />
                </LocalizationProvider>

                <Button onClick={handleResetFilters} disabled={isFiltersEmpty}>
                  {t('filters.resetShort')}
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  maxHeight: '40px',
                  alignItems: 'center',
                  minWidth: 'fit-content',
                  flexWrap: 'nowrap',
                  gap: '.5rem',
                }}
              >
                <ColumnDensityFilter
                  handleDensityChange={setDensity}
                  selectedValue={density}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.only_upcoming}
                      onChange={handleOwnTasksOnlyChange}
                    />
                  }
                  label='Only upcoming'
                />
              </Box>
            </Box>
          </Box>

          <TimeOffTable
            rows={entries}
            filters={filters}
            setFilters={setFilters}
            totalCount={count}
            loading={isLoading}
            disabled={!manageTimeOffPermission}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={setHiddenColumns}
            density={density}
            setDensity={setDensity}
            onResetFilters={handleResetFilters}
          />
        </Box>
      )}
    </Box>
  );
};
